import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	isIntentDetectionSuccess,
	jiraIntentDetectionQuery,
} from '@atlassian/search-client/intent-detection';
import {
	KnowledgeDiscoveryJiraIntentDetectionModel,
	type KnowledgeDiscoveryJiraIntentDetectionModelType,
	KnowledgeDiscoveryJiraSearchExperience,
} from '@atlassian/search-client/knowledge-discovery/types';
import {
	extractTraceInfoFromResponse,
	normaliseTraceInfo,
} from '@atlassian/search-client/network-trace-info';

import { getSessionIdFromLocalStorage } from '../../../../common/utils/events/get-session-id-from-local-storage';
import { remapVitalStatsEventForQuickFind } from '../../../../common/utils/events/remap-vital-stats-event-for-quick-find';
import { sha256Hash } from '../../../../common/utils/events/sha256-hash';
import { TASK_NAMES } from '../../../../common/utils/events/task-names';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../../../common/utils/events/vital-stats-events';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { IntentMode } from '../../../../common/utils/intent-detection';
import {
	onIntentDetectionResponse,
	type IntentDetectionResponseAttributes,
} from '../../../../common/utils/quick-find/events/intent-detection-response';
import { getWordCount } from '../../../../controllers/quick-find/analytics';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { useIntentDetectionVitalStatsTaskState } from '../../../../controllers/quick-find/intent-detection-vitalstats-context';
import {
	useAnalyticsAttributes,
	useCloudId,
	useOrgId,
	usePrimaryProduct,
	useSearchSessionId,
} from '../../../../controllers/store/bootstrap';

import { modelIdValidator } from './model-id-validator';

/**
 * Result returned by the intent detection service callback.
 */
export type IntentDetectionServiceResult = {
	mode: IntentMode;
	modelId?: KnowledgeDiscoveryJiraIntentDetectionModelType;
	patternType?: string;
};

/**
 * Real intent detection via @atlassian/search-client.
 */
export const useIntentDetectionService: (options?: {
	defaultMode?: IntentMode;
}) => (query: string) => Promise<IntentDetectionServiceResult> = ({
	defaultMode = IntentMode.GNIN,
}: { defaultMode?: IntentMode } = {}) => {
	const intl = useIntl();
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const sessionId = getSessionIdFromLocalStorage();
	const taskStateRef = useIntentDetectionVitalStatsTaskState();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
		locale: intl.locale,
	});
	const intentDetectionModelId = intentDetectionExperiment?.modelId;
	const intentDetectionCohort = intentDetectionExperiment?.cohort;
	const [analyticsAttributes] = useAnalyticsAttributes();

	// Memoize atl-attribution headers to avoid recreating on every render
	const atlAttributionHeaders = useMemo(
		() =>
			buildAtlAttributionHeaders({
				cloudId,
				activationId: analyticsAttributes?.activationId,
				product: primaryProduct,
			}),
		[cloudId, analyticsAttributes?.activationId, primaryProduct],
	);

	const validatedModelId = modelIdValidator(intentDetectionModelId)
		? intentDetectionModelId
		: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC;

	const fireAnalytics = useCallback(
		(
			action: 'success' | 'fail',
			attributes: Omit<IntentDetectionResponseAttributes, 'searchSessionId' | 'cohort' | 'modelId'>,
		) => {
			fireAnalyticsEvent(
				onIntentDetectionResponse({
					action,
					attributes: {
						...attributes,
						searchSessionId,
						modelId: validatedModelId,
						cohort: intentDetectionCohort,
					},
				}),
			);
		},
		[searchSessionId, fireAnalyticsEvent, validatedModelId, intentDetectionCohort],
	);

	return useCallback(
		async (query: string): Promise<IntentDetectionServiceResult> => {
			// create siteARI based on primary product
			const siteId = `ari:cloud:${primaryProduct}::site/${cloudId}`;
			// VitalStats: Generate task ID and record start time
			const taskId = crypto.randomUUID();
			const uxTaskId = taskStateRef.current.id;
			const startTime = performance.now();
			// Query Analytics attributes
			const queryHash = sha256Hash(query);
			const queryLength = query.length;
			const wordCount = getWordCount(query);

			fireAnalyticsEvent(
				remapVitalStatsEventForQuickFind(
					onTaskStart({
						searchSessionId: searchSessionId || '',
						taskId,
						task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
						moreContext: {
							uxTaskId,
							queryHash,
							queryLength,
							wordCount,
							cohort: intentDetectionCohort,
							modelId: validatedModelId,
						},
					}),
				),
			);

			try {
				const response = await jiraIntentDetectionQuery(
					{
						query,
						siteId,
						sessionId,
						orgId: orgId ?? '',
						experience: KnowledgeDiscoveryJiraSearchExperience.QUICK_FIND,
						model: validatedModelId,
						includePatternType: true,
					},
					atlAttributionHeaders,
				);

				const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
				const latencyMs = performance.now() - startTime;
				const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
					? normaliseTraceInfo(extractTraceInfoFromResponse(response))
					: {};

				if (!isIntentDetectionSuccess(result)) {
					const errorName = 'UnsuccessfulResponse';
					const errorMessage = 'Query typename mismatch';

					fireAnalytics('fail', {
						queryHash,
						errorName,
						errorMessage,
						queryLength,
						wordCount,
						queryClassification: null,
					});
					fireAnalyticsEvent(
						remapVitalStatsEventForQuickFind(
							onTaskFail({
								searchSessionId: searchSessionId || '',
								taskId,
								task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
								moreContext: {
									uxTaskId,
									queryHash,
									queryLength,
									wordCount,
									cohort: intentDetectionCohort,
									queryClassification: null,
									latencyMs,
									errorName,
									errorMessage,
									modelId: validatedModelId,
									...traceInfo,
								},
							}),
						),
					);

					return { mode: defaultMode };
				}

				const intentMode = result.searchClassification?.type || defaultMode;
				const patternType = result.searchClassification?.pattern ?? undefined;

				fireAnalytics('success', {
					queryHash,
					queryLength,
					wordCount,
					queryClassification: intentMode,
					patternType,
				});
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskSuccess({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								uxTaskId,
								queryHash,
								queryLength,
								wordCount,
								cohort: intentDetectionCohort,
								latencyMs,
								queryClassification: intentMode,
								modelId: validatedModelId,
								patternType,
								...traceInfo,
							},
						}),
					),
				);

				return {
					mode: intentMode,
					modelId: validatedModelId,
					patternType,
				};
			} catch (error) {
				const latencyMs = performance.now() - startTime;
				const errorName = (error as Error)?.name || 'UnknownError';
				const errorMessage = (error as Error)?.message ?? 'An unknown error occurred';

				fireAnalytics('fail', {
					queryHash,
					errorName,
					errorMessage,
					queryLength,
					wordCount,
					queryClassification: null,
				});
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskFail({
							searchSessionId: searchSessionId || '',
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_NETWORK,
							moreContext: {
								uxTaskId,
								queryHash,
								queryLength,
								wordCount,
								cohort: intentDetectionCohort,
								queryClassification: null,
								latencyMs,
								errorName: (error as Error)?.name,
								errorMessage: (error as Error)?.message,
								modelId: validatedModelId,
								...(fg('rovo-search-vitalstats-tracing-headers')
									? normaliseTraceInfo(undefined)
									: {}),
							},
						}),
					),
				);

				return { mode: defaultMode };
			}
		},
		[
			cloudId,
			orgId,
			primaryProduct,
			defaultMode,
			fireAnalytics,
			searchSessionId,
			sessionId,
			fireAnalyticsEvent,
			validatedModelId,
			intentDetectionCohort,
			taskStateRef,
			atlAttributionHeaders,
		],
	);
};
